import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { SPRING } from '@/lib/motion'

const SERVICES = [
  {
    name: 'Home Deep Clean',
    amharic: 'ቤት ጥልቅ ፅዳት',
    description: 'Complete residential deep cleaning. Every corner, every surface, sparkling clean.',
    span: 3
  },
  {
    name: 'Sofa & Upholstery',
    amharic: 'ሶፋ ፅዳት',
    description: 'All sofa types. Fabric extraction technology for deep stain removal.',
    span: 3
  },
  {
    name: 'Mattress Sanitize',
    amharic: 'ፍራሽ ፅዳት',
    description: 'UV treatment + deep extraction. Sleep better on a sanitized bed.',
    span: 2
  },
  {
    name: 'Office & Commercial',
    amharic: 'ቢሮ ፅዳት',
    description: 'Scheduled corporate cleaning. Maintain a professional workspace.',
    span: 2
  },
  {
    name: 'Post-Construction',
    amharic: 'ግንባታ ፅዳት',
    description: 'Move-in ready cleaning. Remove dust, debris, and construction residue.',
    span: 2
  },
  {
    name: 'Window & Glass',
    amharic: 'መስኮት ፅዳት',
    description: 'Streak-free inside + out. Crystal clear views guaranteed.',
    span: 3
  },
  {
    name: 'Holiday Package',
    amharic: 'በዓል ፅዳት',
    description: 'Enkutatash · Gena · Fasika. Special holiday preparation cleaning.',
    span: 3
  },
  {
    name: 'Sanitization',
    amharic: 'ንፅህና',
    description: 'Medical-grade disinfection for homes, offices, and commercial spaces. Health first.',
    span: 6
  }
]

function ServiceCard({
  service,
  index
}: {
  service: (typeof SERVICES)[0]
  index: number
}) {
  return (
    <motion.div
      data-cursor="hover"
      initial={{ opacity: 0, y: 60, filter: 'blur(8px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0)' }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ ...SPRING, delay: index * 0.06 }}
      whileHover={{ y: -6, scale: 1.01 }}
      style={{
        gridColumn: `span ${service.span}`,
        background: 'var(--elevated)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 20,
        padding: '28px 28px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        minHeight: service.span === 6 ? 180 : 240
      }}
    >
      {/* Hover lime flash */}
      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(232,255,0,0.03)',
          opacity: 0
        }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      />

      {/* Top border line */}
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '2px',
          background: 'var(--lime)',
          width: '0%'
        }}
        whileHover={{ width: '100%' }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      />

      {/* Service number */}
      <div
        style={{
          fontFamily: 'Bebas Neue',
          fontSize: '1rem',
          color: 'rgba(232,255,0,0.25)',
          letterSpacing: '0.1em'
        }}
      >
        0{index + 1}
      </div>

      {/* Name */}
      <div
        style={{
          fontFamily: 'DM Sans',
          fontWeight: 800,
          fontSize: service.span === 6 ? '1.5rem' : '1.2rem',
          color: 'var(--white)',
          lineHeight: 1.2
        }}
      >
        {service.name}
      </div>

      {/* Amharic name */}
      <div
        style={{
          fontFamily: 'DM Sans',
          fontWeight: 500,
          fontSize: '0.9rem',
          color: 'rgba(232,255,0,0.6)'
        }}
      >
        {service.amharic}
      </div>

      {/* Description */}
      <p
        style={{
          fontFamily: 'DM Sans',
          fontWeight: 400,
          fontSize: '0.88rem',
          lineHeight: 1.65,
          color: 'var(--white-60)',
          marginTop: 4,
          maxWidth: service.span === 6 ? 600 : 'none'
        }}
      >
        {service.description}
      </p>

      {/* Arrow */}
      <motion.div
        style={{
          marginTop: 'auto',
          color: 'var(--lime)',
          fontSize: '1.2rem',
          alignSelf: 'flex-end'
        }}
        initial={{ x: 0, opacity: 0.4 }}
        whileHover={{ x: 6, opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        →
      </motion.div>
    </motion.div>
  )
}

export function Services() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section
      ref={ref}
      style={{
        background: 'var(--surface)',
        padding: 'var(--pad-section) max(5vw, 24px)'
      }}
    >
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          style={{ marginBottom: 48 }}
        >
          <span
            style={{
              fontFamily: 'DM Sans',
              fontSize: '0.8rem',
              color: 'var(--lime)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase'
            }}
          >
            What We Offer · የምናቀርባቸው አገልግሎቶች
          </span>
          <h2
            style={{
              fontFamily: 'Bebas Neue',
              fontSize: 'var(--h2)',
              color: 'var(--white)',
              marginTop: 12,
              lineHeight: 1
            }}
          >
            Our Services
          </h2>
          <p
            style={{
              fontFamily: 'DM Sans',
              fontSize: '1rem',
              color: 'var(--white-60)',
              marginTop: 16,
              maxWidth: 500
            }}
          >
            From daily maintenance to deep cleaning projects. We handle it all with professional
            equipment and trained staff.
          </p>
        </motion.div>

        {/* Bento grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 16
          }}
        >
          {SERVICES.map((service, i) => (
            <ServiceCard key={service.name} service={service} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}
